import { CoachRow } from './tabData';
import { MetricKey, checkSample } from './coachAnalysis';

/**
 * 종합 프로파일 레이더 — 강사 리포트 1장의 '05 종합 프로파일'에 들어간다.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 별도 파일인가
 * ─────────────────────────────────────────────────────────────
 * 예전에는 CoachDetailDrawer(우측 슬라이드 패널) 안에 살면서
 * CoachPdfReport 가 그 패널에서 import 해 갔습니다. 패널은 완성본 리포트로
 * 대체되어 사라졌는데, 레이더는 리포트가 계속 씁니다.
 * 화면 컴포넌트에 얹혀 있으면 그 화면이 사라질 때마다 같이 흔들리므로
 * 그리기만 하는 조각으로 떼어 둡니다.
 */

/** 육각 레이더 — 축을 한 눈에 */
export const RadarChart = ({
    values,
    size = 200,
    color = '#3D73DD',
    /** 점수를 축 옆에 같이 찍는다 (인쇄물에서는 숫자가 있어야 근거가 된다) */
    showScores = false,
}: {
    /** 못 잰 축은 null. 0 으로 그리면 "이게 최악"으로 읽힌다 */
    values: { label: string; score: number | null }[];
    size?: number;
    color?: string;
    showScores?: boolean;
}) => {
    /*
     * 라벨이 잘리던 문제.
     * 예전에는 width=height=size 인 상자에 반지름 1.24 배 위치로 글자를 찍어서
     * 바깥쪽 라벨이 SVG 밖으로 나가 잘렸습니다. viewBox 에 여백을 따로 주고
     * 그 안에서 그립니다 — 라벨이 몇 글자든 잘리지 않습니다.
     */
    /*
     * 좌우는 '출석관리' 같은 네 글자 라벨이 도형 밖으로 뻗고,
     * 위아래는 라벨+점수 두 줄이면 충분하다. 사방을 34로 잡았더니
     * 세로로 8mm 를 그냥 버려 1장이 넘쳤다.
     */
    const PAD = 34;
    const PAD_Y = 22;
    const box = size + PAD * 2;
    const boxH = size + PAD_Y * 2;
    const cx = box / 2;
    const cy = boxH / 2;
    const r = size / 2;
    const n = values.length;
    const pt = (i: number, ratio: number) => {
        const ang = (Math.PI * 2 * i) / n - Math.PI / 2;
        return [cx + Math.cos(ang) * r * ratio, cy + Math.sin(ang) * r * ratio] as const;
    };

    /*
     * 못 잰 축은 도형을 안쪽으로 끌어당기지 않도록 축에서 제외한다.
     * 0으로 찍으면 그 방향이 푹 패여서 "여기가 약점"으로 보인다.
     */
    const poly = values
        .map((v, i) => (v.score == null ? null : pt(i, Math.max(v.score, 0) / 100).join(',')))
        .filter(Boolean)
        .join(' ');

    return (
        <svg
            width={box}
            height={boxH}
            viewBox={`0 0 ${box} ${boxH}`}
            aria-hidden="true"
            style={{ flex: 'none', overflow: 'visible' }}
        >
            {[0.25, 0.5, 0.75, 1].map((g) => (
                <polygon
                    key={g}
                    points={values.map((_, i) => pt(i, g).join(',')).join(' ')}
                    fill={g === 1 ? '#FBFCFE' : 'none'}
                    stroke="#DFE7F2"
                    strokeWidth={g === 1 ? 1.4 : 1}
                />
            ))}
            {values.map((_, i) => {
                const [x, y] = pt(i, 1);
                return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#DFE7F2" strokeWidth="1" />;
            })}

            {/*
              눈금 숫자(50/100)를 중앙 세로선에 찍었더니 맨 위 축 라벨과 겹쳤다.
              축마다 점수를 이미 찍고 있으므로 눈금 숫자는 빼고,
              '바깥 테두리 = 100점'만 섹션 설명에 적는다.
            */}
            <polygon points={poly} fill={color} fillOpacity="0.2" stroke={color} strokeWidth="2.4" />

            {values.map((v, i) => {
                // 못 잰 축에는 점을 찍지 않는다
                if (v.score == null) return null;
                const [x, y] = pt(i, v.score / 100);
                return <circle key={i} cx={x} cy={y} r="4" fill="#FFFFFF" stroke={color} strokeWidth="2.4" />;
            })}

            {values.map((v, i) => {
                const [x, y] = pt(i, 1.2);
                // 좌우 축은 바깥쪽으로 정렬해 도형과 안 겹치게 한다
                const anchor = Math.abs(x - cx) < 6 ? 'middle' : x > cx ? 'start' : 'end';
                return (
                    <g key={v.label}>
                        <text
                            x={x}
                            y={showScores ? y - 5 : y}
                            textAnchor={anchor}
                            dominantBaseline="middle"
                            style={{ fontSize: 11, fill: '#5B6B7F', fontWeight: 600 }}
                        >
                            {v.label}
                        </text>
                        {showScores && (
                            <text
                                x={x}
                                y={y + 7}
                                textAnchor={anchor}
                                dominantBaseline="middle"
                                style={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    fill:
                                        v.score == null
                                            ? '#C9D3E0'
                                            : v.score >= 70
                                              ? '#00795F'
                                              : v.score >= 45
                                                ? '#3D73DD'
                                                : '#C22D38',
                                }}
                            >
                                {v.score == null ? '—' : Math.round(v.score)}
                            </text>
                        )}
                    </g>
                );
            })}
        </svg>
    );
};

/**
 * 종합 프로파일 축.
 *
 * '응답속도'와 '약속이행'을 축으로 두고 있었는데, 서버가 그 둘을 안 보냅니다
 * (responseHours / sameDayCancel — 집계 경로 없음). 목 데이터에만 값이 있어
 * 화면에서는 육각형이 그려졌지만 실제 센터에서는 두 축이 늘 비어 있었습니다.
 * 라포가 실제로 재는 넷만 남깁니다. 서버가 집계를 붙이면 되돌리면 됩니다.
 *
 * 못 잰 축은 null 입니다. 0 으로 그리면 그 축이 가장 안쪽으로 찍혀
 * "이 강사는 이게 최악"으로 읽힙니다. 실제로는 아직 재지 않았을 뿐입니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 환산식을 축 정의로 꺼냈나
 * ─────────────────────────────────────────────────────────────
 * 리포트가 축마다 **센터 평균도 같은 자로 환산해서** 옆에 세웁니다.
 * 환산식이 buildRadar 안에 인라인으로 박혀 있으면 평균 쪽에 같은 식을
 * 한 번 더 적게 되고, 둘 중 하나만 고치는 날 강사 점수와 평균 점수가
 * 다른 자로 재집니다 — 그 표는 인쇄돼 면담 자리에 놓입니다.
 * 자는 하나만 둡니다.
 */
export type RadarAxis = {
    /**
     * 이 축이 어느 지표에서 나왔는가.
     * 순위는 여기서 03 지표 평가 표의 값을 그대로 가져옵니다 —
     * 순위 규칙을 두 번 적으면 같은 지면에서 두 숫자가 어긋납니다.
     */
    metricKey: MetricKey;
    label: string;
    /**
     * 원값이 축 이름과 **반대 방향**일 때 원값 앞에 붙일 이름 (2026-09-03 대표 QA ③).
     *
     * 🔴 '출석관리' 축의 원값은 **노쇼율**이다. 이름 없이 원값만 적으면
     *    「출석관리 0% · 100점」이 되어, 종이를 받은 강사는 "출석률이 0인데
     *    만점"으로 읽는다. 실제로 대표 리포트가 그렇게 인쇄됐다.
     *    03 지표 평가 표는 같은 값을 「담당 회원 노쇼율 0%」로 정확히 적는데,
     *    한 장 안에서 같은 숫자가 두 이름으로 나가면 안 된다.
     */
    rawLabel?: string;
    /**
     * 표본이 모자라면 유보할 축인가 (2026-09-03 대표 QA ③).
     *
     * 🔴 비율 축은 **분모가 수업 수**다. 수업 3회짜리 강사의 노쇼 0%·일지 100%는
     *    실력이 아니라 표본 크기의 부작용이고, 그대로 환산하면 만점이 된다.
     *    같은 지면 표지에는 이미 「표본 부족으로 평가 유보」가 찍히는데
     *    05 표에만 100점·초록·「▲ 강점」이 나가 서로 모순이었다.
     *    reportComposer 의 성숙도 판정 주석이 정확히 이 사고를 적어 두고
     *    등급만 막아 뒀다 — 레이더가 그 관문 밖에 남아 있었다.
     *
     * 수업량 축은 **표본 그 자체**라 게이트를 걸지 않는다.
     * 3회를 3회라고 적는 것은 언제나 참이다.
     */
    needsSample: boolean;
    /** 이 강사의 원값 */
    rawOf: (c: CoachRow) => number | null;
    /** 원값 → 0~100 환산. 강사 값과 센터 평균에 **같은 함수**를 쓴다 */
    normalize: (raw: number | null | undefined) => number | null;
};

export const RADAR_AXES: RadarAxis[] = [
    {
        metricKey: 'conversion',
        /* 🔴 이름은 계산기(`coachAnalysis.METRICS`)와 같은 말이어야 한다 — 「전환」은 다른 지표로 읽힌다 */
        label: '재등록',
        /* 그 달 정상 종료 회원이 분모다 — 표본이 얇으면 100%도 1명에서 나온다 */
        needsSample: true,
        rawOf: (c) => c.conversionPct,
        normalize: (v) => (v == null ? null : Math.min((v / 60) * 100, 100)),
    },
    {
        metricKey: 'lessons',
        label: '수업량',
        /* 이 축이 곧 표본이다. 스스로를 게이트로 막으면 아무것도 안 남는다 */
        needsSample: false,
        rawOf: (c) => c.lessons,
        normalize: (v) => (v == null ? null : Math.min((v / 200) * 100, 100)),
    },
    {
        metricKey: 'journal',
        label: '일지',
        /* 수업 수가 분모 — 3회 중 3회 쓰면 100% */
        needsSample: true,
        rawOf: (c) => c.journalPct,
        normalize: (v) => v ?? null,
    },
    {
        /* 노쇼는 낮을수록 좋다 — 점수는 뒤집어서 매긴다 */
        metricKey: 'noshow',
        label: '출석관리',
        /* ⚠️ 축 이름과 원값의 방향이 반대다. 원값에 반드시 이름을 붙인다 */
        rawLabel: '노쇼',
        /* 수업 수가 분모 — 3회 다 나오면 노쇼 0% = 만점 */
        needsSample: true,
        rawOf: (c) => c.noshowPct,
        normalize: (v) => (v == null ? null : Math.max(100 - v * 18, 0)),
    },
];

/**
 * 이 강사의 축 점수 — **표본 게이트를 통과한 것만**.
 *
 * 🔴 화면(CoachDataDrawer)과 인쇄물(CoachPdfReport)이 **이 함수 하나만** 부른다.
 *    예전에는 인쇄물이 `axis.normalize(axis.rawOf(coach))` 를 따로 적고 있어서,
 *    한쪽에 게이트를 걸면 다른 쪽만 조용히 만점을 그렸다.
 *    같은 식을 두 번 쓰지 않는다 — 복사본은 낡고, 화면마다 답이 갈린다.
 *
 * 유보는 **null** 이다. 0 이 아니다 — 0 으로 그리면 그 축이 가장 안쪽에 찍혀
 * "이게 최악"으로 읽힌다. 이 저장소가 이미 쓰는 원칙 그대로다.
 */
export const axisScoreOf = (axis: RadarAxis, c: CoachRow): number | null => {
    if (axis.needsSample && !checkSample(c).enough) return null;
    return axis.normalize(axis.rawOf(c));
};

export const buildRadar = (c: CoachRow): { label: string; score: number | null }[] =>
    RADAR_AXES.map((a) => ({ label: a.label, score: axisScoreOf(a, c) }));
